import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { useSelector } from 'react-redux';
import SUVSelectedIcon from '../assets/icons/FilterBodyTypeOptions/SUVSelectedIcon';
import SUVDefaultIcon from '../assets/icons/FilterBodyTypeOptions/SUVDefaultIcon';
import SedanSelectedIcon from '../assets/icons/FilterBodyTypeOptions/SedanSelectedIcon';
import SedanDefaultIcon from '../assets/icons/FilterBodyTypeOptions/SedanDefaultIcon';
import AllroadQuattroSelectedIcon from '../assets/icons/FilterBodyTypeOptions/AllroadQuattroSelectedIcon';
import AllroadQuattroDefaultIcon from '../assets/icons/FilterBodyTypeOptions/AllroadQuattroDefaultIcon';
import CoupeSelectedIcon from '../assets/icons/FilterBodyTypeOptions/CoupeSelectedIcon';
import CoupeDefaultIcon from '../assets/icons/FilterBodyTypeOptions/CoupeDefaultIcon';
import SportbackSelectedIcon from '../assets/icons/FilterBodyTypeOptions/SportbackSelectedIcon';
import SportbackDefaultIcon from '../assets/icons/FilterBodyTypeOptions/SportbackDefaultIcon';
import SpyderSelectedIcon from '../assets/icons/FilterBodyTypeOptions/SpyderSelectedIcon';
import SpyderDefaultIcon from '../assets/icons/FilterBodyTypeOptions/SpyderDefaultIcon';
import RoadsterSelectedIcon from '../assets/icons/FilterBodyTypeOptions/RoadsterSelectedIcon';
import RoadsterDefaultIcon from '../assets/icons/FilterBodyTypeOptions/RoadsterDefaultIcon';
import CabrioletSelectedIcon from '../assets/icons/FilterBodyTypeOptions/CabrioletSelectedIcon';
import CabrioletDefaultIcon from '../assets/icons/FilterBodyTypeOptions/CabrioletDefaultIcon';
import AvantSelectedIcon from '../assets/icons/FilterBodyTypeOptions/AvantSelectedIcon';
import AvantDefaultIcon from '../assets/icons/FilterBodyTypeOptions/AvantDefaultIcon';
import { selectOffers } from '../reducers/offersReducer';
import { selectFilters } from '../reducers/filtersReducer';
import { selectZipCode } from '../reducers/zipCodeReducer';

const useFilterOptions = () => {
  const { t } = useTranslation();

  const { offersList } = useSelector(selectOffers);
  const { confirmedFilters } = useSelector(selectFilters);
  const { market } = useSelector(selectZipCode);

  const isCaMarket = market === 'CA';

  const modelFamilyOptions = useMemo(
    () => ({
      etrongt: {
        label: t('promo.filters.filterOptions.modelFamily.etrongt'),
        ariaLabel: t('promo.filters.filterOptions.modelFamily.etrongt'),
        value: 'etrongt',
        tabs: ['etron'],
      },
      rsetrongt: {
        label: t('promo.filters.filterOptions.modelFamily.rsetrongt'),
        ariaLabel: t('promo.filters.filterOptions.modelFamily.rsetrongt'),
        value: 'rsetrongt',
        tabs: ['etron'],
      },
      a6etron: {
        label: t('promo.filters.filterOptions.modelFamily.a6etron'),
        ariaLabel: t('promo.filters.filterOptions.modelFamily.a6etron'),
        value: 'a6etron',
        tabs: isCaMarket ? ['etron'] : ['a/s'],
      },
      s6etron: {
        label: t('promo.filters.filterOptions.modelFamily.s6etron'),
        ariaLabel: t('promo.filters.filterOptions.modelFamily.s6etron'),
        value: 's6etron',
        tabs: isCaMarket ? ['etron'] : ['a/s'],
      },
      q4etron: {
        label: t('promo.filters.filterOptions.modelFamily.q4etron'),
        ariaLabel: t('promo.filters.filterOptions.modelFamily.q4etron'),
        value: 'q4etron',
        tabs: isCaMarket ? ['etron'] : ['q/sq'],
      },
      q6etron: {
        label: t('promo.filters.filterOptions.modelFamily.q6etron'),
        ariaLabel: t('promo.filters.filterOptions.modelFamily.q6etron'),
        value: 'q6etron',
        tabs: isCaMarket ? ['etron'] : ['q/sq'],
      },
      sq6etron: {
        label: t('promo.filters.filterOptions.modelFamily.sq6etron'),
        ariaLabel: t('promo.filters.filterOptions.modelFamily.sq6etron'),
        value: 'sq6etron',
        tabs: isCaMarket ? ['etron'] : ['q/sq'],
      },
      q8etron: {
        label: t('promo.filters.filterOptions.modelFamily.q8etron'),
        ariaLabel: t('promo.filters.filterOptions.modelFamily.q8etron'),
        value: 'q8etron',
        tabs: isCaMarket ? ['etron'] : ['q/sq'],
      },
      sq8etron: {
        label: t('promo.filters.filterOptions.modelFamily.sq8etron'),
        ariaLabel: t('promo.filters.filterOptions.modelFamily.sq8etron'),
        value: 'sq8etron',
        tabs: isCaMarket ? ['etron'] : ['q/sq'],
      },
      q3: {
        label: t('promo.filters.filterOptions.modelFamily.q3'),
        ariaLabel: t('promo.filters.filterOptions.modelFamily.q3'),
        value: 'q3',
        tabs: ['q/sq'],
      },
      ...(isCaMarket
        ? {
            q5new: {
              label: t('promo.filters.filterOptions.modelFamily.q5new'),
              ariaLabel: t('promo.filters.filterOptions.modelFamily.q5new'),
              value: 'q5new',
              tabs: ['q/sq'],
            },
          }
        : {}),
      ...(isCaMarket
        ? {
            sq5new: {
              label: t('promo.filters.filterOptions.modelFamily.sq5new'),
              ariaLabel: t('promo.filters.filterOptions.modelFamily.sq5new'),
              value: 'sq5new',
              tabs: ['q/sq'],
            },
          }
        : {}),
      q5: {
        label: t('promo.filters.filterOptions.modelFamily.q5'),
        ariaLabel: t('promo.filters.filterOptions.modelFamily.q5'),
        value: 'q5',
        tabs: ['q/sq'],
      },
      q5e: {
        label: t('promo.filters.filterOptions.modelFamily.q5e'),
        ariaLabel: t('promo.filters.filterOptions.modelFamily.q5e'),
        value: 'q5e',
        tabs: ['q/sq'],
      },
      sq5: {
        label: t('promo.filters.filterOptions.modelFamily.sq5'),
        ariaLabel: t('promo.filters.filterOptions.modelFamily.sq5'),
        value: 'sq5',
        tabs: ['q/sq'],
      },
      q7: {
        label: t('promo.filters.filterOptions.modelFamily.q7'),
        ariaLabel: t('promo.filters.filterOptions.modelFamily.q7'),
        value: 'q7',
        tabs: ['q/sq'],
      },
      sq7: {
        label: t('promo.filters.filterOptions.modelFamily.sq7'),
        ariaLabel: t('promo.filters.filterOptions.modelFamily.sq7'),
        value: 'sq7',
        tabs: ['q/sq'],
      },
      q8: {
        label: t('promo.filters.filterOptions.modelFamily.q8'),
        ariaLabel: t('promo.filters.filterOptions.modelFamily.q8'),
        value: 'q8',
        tabs: ['q/sq'],
      },
      sq8: {
        label: t('promo.filters.filterOptions.modelFamily.sq8'),
        ariaLabel: t('promo.filters.filterOptions.modelFamily.sq8'),
        value: 'sq8',
        tabs: ['q/sq'],
      },
      rsq8: {
        label: t('promo.filters.filterOptions.modelFamily.rsq8'),
        ariaLabel: t('promo.filters.filterOptions.modelFamily.rsq8'),
        value: 'rsq8',
        tabs: ['q/sq'],
      },
      a3: {
        label: t('promo.filters.filterOptions.modelFamily.a3'),
        ariaLabel: t('promo.filters.filterOptions.modelFamily.a3'),
        value: 'a3',
        tabs: ['a/s'],
      },
      s3: {
        label: t('promo.filters.filterOptions.modelFamily.s3'),
        ariaLabel: t('promo.filters.filterOptions.modelFamily.s3'),
        value: 's3',
        tabs: ['a/s'],
      },
      rs3: {
        label: t('promo.filters.filterOptions.modelFamily.rs3'),
        ariaLabel: t('promo.filters.filterOptions.modelFamily.rs3'),
        value: 'rs3',
        tabs: ['a/s'],
      },
      a4: {
        label: t('promo.filters.filterOptions.modelFamily.a4'),
        ariaLabel: t('promo.filters.filterOptions.modelFamily.a4'),
        value: 'a4',
        tabs: ['a/s'],
      },
      s4: {
        label: t('promo.filters.filterOptions.modelFamily.s4'),
        ariaLabel: t('promo.filters.filterOptions.modelFamily.s4'),
        value: 's4',
        tabs: ['a/s'],
      },
      ...(isCaMarket
        ? {
            a5new: {
              label: t('promo.filters.filterOptions.modelFamily.a5new'),
              ariaLabel: t('promo.filters.filterOptions.modelFamily.a5new'),
              value: 'a5new',
              tabs: ['a/s'],
            },
          }
        : {}),
      ...(isCaMarket
        ? {
            s5new: {
              label: t('promo.filters.filterOptions.modelFamily.s5new'),
              ariaLabel: t('promo.filters.filterOptions.modelFamily.s5new'),
              value: 's5new',
              tabs: ['a/s'],
            },
          }
        : {}),
      a5: {
        label: t('promo.filters.filterOptions.modelFamily.a5'),
        ariaLabel: t('promo.filters.filterOptions.modelFamily.a5'),
        value: 'a5',
        tabs: ['a/s'],
      },
      s5: {
        label: t('promo.filters.filterOptions.modelFamily.s5'),
        ariaLabel: t('promo.filters.filterOptions.modelFamily.s5'),
        value: 's5',
        tabs: ['a/s'],
      },
      rs5: {
        label: t('promo.filters.filterOptions.modelFamily.rs5'),
        ariaLabel: t('promo.filters.filterOptions.modelFamily.rs5'),
        value: 'rs5',
        tabs: ['a/s'],
      },
      a6: {
        label: t('promo.filters.filterOptions.modelFamily.a6'),
        ariaLabel: t('promo.filters.filterOptions.modelFamily.a6'),
        value: 'a6',
        tabs: ['a/s'],
      },
      s6: {
        label: t('promo.filters.filterOptions.modelFamily.s6'),
        ariaLabel: t('promo.filters.filterOptions.modelFamily.s6'),
        value: 's6',
        tabs: ['a/s'],
      },
      rs6: {
        label: t('promo.filters.filterOptions.modelFamily.rs6'),
        ariaLabel: t('promo.filters.filterOptions.modelFamily.rs6'),
        value: 'rs6',
        tabs: ['a/s'],
      },

      a7: {
        label: t('promo.filters.filterOptions.modelFamily.a7'),
        ariaLabel: t('promo.filters.filterOptions.modelFamily.a7'),
        value: 'a7',
        tabs: ['a/s'],
      },
      s7: {
        label: t('promo.filters.filterOptions.modelFamily.s7'),
        ariaLabel: t('promo.filters.filterOptions.modelFamily.s7'),
        value: 's7',
        tabs: ['a/s'],
      },
      rs7: {
        label: t('promo.filters.filterOptions.modelFamily.rs7'),
        ariaLabel: t('promo.filters.filterOptions.modelFamily.rs7'),
        value: 'rs7',
        tabs: ['a/s'],
      },
      a8: {
        label: t('promo.filters.filterOptions.modelFamily.a8'),
        ariaLabel: t('promo.filters.filterOptions.modelFamily.a8'),
        value: 'a8',
        tabs: ['a/s'],
      },
      s8: {
        label: t('promo.filters.filterOptions.modelFamily.s8'),
        ariaLabel: t('promo.filters.filterOptions.modelFamily.s8'),
        value: 's8',
        tabs: ['a/s'],
      },
    }),
    [isCaMarket, t],
  );

  const bodyTypeOptions = useMemo(
    () => ({
      suv: {
        label: t('promo.filters.filterOptions.bodyType.suv'),
        ariaLabel: t('promo.filters.filterOptions.bodyType.suv'),
        value: 'suv',
        image: SUVDefaultIcon,
        selectedImage: SUVSelectedIcon,
      },
      sedan: {
        label: t('promo.filters.filterOptions.bodyType.sedan'),
        ariaLabel: t('promo.filters.filterOptions.bodyType.sedan'),
        value: 'sedan',
        image: SedanDefaultIcon,
        selectedImage: SedanSelectedIcon,
      },
      sportback: {
        label: t('promo.filters.filterOptions.bodyType.sportback'),
        ariaLabel: t('promo.filters.filterOptions.bodyType.sportback'),
        value: 'sportback',
        image: SportbackDefaultIcon,
        selectedImage: SportbackSelectedIcon,
      },
      'allroad quattro': {
        label: t('promo.filters.filterOptions.bodyType.allroad'),
        ariaLabel: t('promo.filters.filterOptions.bodyType.allroad'),
        value: 'allroad quattro',
        image: AllroadQuattroDefaultIcon,
        selectedImage: AllroadQuattroSelectedIcon,
      },
      avant: {
        label: t('promo.filters.filterOptions.bodyType.avant'),
        ariaLabel: t('promo.filters.filterOptions.bodyType.avant'),
        value: 'avant',
        image: AvantDefaultIcon,
        selectedImage: AvantSelectedIcon,
      },
      coupe: {
        label: t('promo.filters.filterOptions.bodyType.coupeUSA'),
        ariaLabel: t('promo.filters.filterOptions.bodyType.coupeUSA'),
        value: 'coupe',
        image: CoupeDefaultIcon,
        selectedImage: CoupeSelectedIcon,
      },
      roadster: {
        label: t('promo.filters.filterOptions.bodyType.roadster'),
        ariaLabel: t('promo.filters.filterOptions.bodyType.roadster'),
        value: 'roadster',
        image: RoadsterDefaultIcon,
        selectedImage: RoadsterSelectedIcon,
      },
      cabriolet: {
        label: t('promo.filters.filterOptions.bodyType.cabriolet'),
        ariaLabel: t('promo.filters.filterOptions.bodyType.cabriolet'),
        value: 'cabriolet',
        image: CabrioletDefaultIcon,
        selectedImage: CabrioletSelectedIcon,
      },
      spyder: {
        label: t('promo.filters.filterOptions.bodyType.spyder'),
        ariaLabel: t('promo.filters.filterOptions.bodyType.spyder'),
        value: 'spyder',
        image: SpyderDefaultIcon,
        selectedImage: SpyderSelectedIcon,
      },
    }),
    [t],
  );

  const trimLineOptions = useMemo(() => {
    const usMarketOption = {
      premium: {
        label: t('promo.filters.filterOptions.trimLine.premium'),
        ariaLabel: t('promo.filters.filterOptions.trimLine.premium'),
        value: 'premium',
        hint: t('promo.filters.filterOptions.trimLine.premiumHint'),
      },
      premium_plus: {
        label: t('promo.filters.filterOptions.trimLine.premiumPlus'),
        ariaLabel: t('promo.filters.filterOptions.trimLine.premiumPlus'),
        value: 'premium_plus',
        hint: t('promo.filters.filterOptions.trimLine.premiumPlusHint'),
      },
      prestige: {
        label: t('promo.filters.filterOptions.trimLine.prestige'),
        ariaLabel: t('promo.filters.filterOptions.trimLine.prestige'),
        value: 'prestige',
        hint: t('promo.filters.filterOptions.trimLine.prestigeHint'),
      },
      base: {
        label: t('promo.filters.filterOptions.trimLine.other'),
        ariaLabel: t('promo.filters.filterOptions.trimLine.other'),
        value: 'base',
        hint: t('promo.filters.filterOptions.trimLine.otherHint'),
      },
    };

    return usMarketOption;
  }, [t]);

  const vehicleTypeOptions = useMemo(
    () => ({
      'NEW-CAR': {
        label: t('promo.filters.filterOptions.vehicleType.new'),
        ariaLabel: t('promo.filters.filterOptions.vehicleType.new'),
        value: 'NEW-CAR',
      },
      'USED-CAR': {
        label: t('promo.filters.filterOptions.vehicleType.used'),
        ariaLabel: t('promo.filters.filterOptions.vehicleType.used'),
        value: 'USED-CAR',
      },
    }),
    [t],
  );

  const offerTypeOptions = useMemo(
    () => ({
      lease: {
        label: t('promo.filters.filterOptions.offerType.lease'),
        ariaLabel: t('promo.filters.filterOptions.offerType.lease'),
        value: 'lease',
      },
      finance: {
        label: t('promo.filters.filterOptions.offerType.finance'),
        ariaLabel: t('promo.filters.filterOptions.offerType.finance'),
        value: 'finance',
      },
      credit: {
        label: t('promo.filters.filterOptions.offerType.credit'),
        ariaLabel: t('promo.filters.filterOptions.offerType.credit'),
        value: 'credit',
      },
    }),
    [t],
  );

  const offerRegionOptions = useMemo(
    () => ({
      VAN: {
        label: t('promo.filters.filterOptions.offerRegion.VAN'),
        ariaLabel: t('promo.filters.filterOptions.offerRegion.VAN'),
        value: 'VAN',
      },
      GTA: {
        label: t('promo.filters.filterOptions.offerRegion.GTA'),
        ariaLabel: t('promo.filters.filterOptions.offerRegion.GTA'),
        value: 'GTA',
      },
      MTL: {
        label: t('promo.filters.filterOptions.offerRegion.MTL'),
        ariaLabel: t('promo.filters.filterOptions.offerRegion.MTL'),
        value: 'MTL',
      },
      ALB: {
        label: t('promo.filters.filterOptions.offerRegion.ALB'),
        ariaLabel: t('promo.filters.filterOptions.offerRegion.ALB'),
        value: 'ALB',
      },
      NAT: {
        label: t('promo.filters.filterOptions.offerRegion.NAT'),
        ariaLabel: t('promo.filters.filterOptions.offerRegion.NAT'),
        value: 'NAT',
        shouldHide: true,
      },
    }),
    [t],
  );

  const modelYearOptions = useMemo(() => {
    const dynamicYears = [
      ...new Set(
        offersList
          .filter(
            (toCheck) => toCheck.offerModelDetail.vehicleType === confirmedFilters.vehicleType,
          )
          .map((offer) => offer.offerModelDetail.modelYear)
          .sort((a, b) => b - a),
      ),
    ];
    if (dynamicYears.length) {
      return Object.fromEntries(
        dynamicYears.map((year) => [
          year,
          { label: year.toString(), ariaLabel: year.toString(), value: year.toString() },
        ]),
      );
    }
    return {
      2026: {
        label: '2026',
        ariaLabel: '2026',
        value: '2026',
      },
      2025: {
        label: '2025',
        ariaLabel: '2025',
        value: '2025',
      },
    };
  }, [offersList, confirmedFilters.vehicleType]);

  const listOfAllFilters = {
    modelFamily: modelFamilyOptions,
    bodyType: bodyTypeOptions,
    trimLine: trimLineOptions,
    modelYear: modelYearOptions,
    offerType: offerTypeOptions,
    vehicleType: vehicleTypeOptions,
    offerRegion: offerRegionOptions,
  };

  return {
    listOfAllFilters,
    modelFamilyOptions,
    bodyTypeOptions,
    trimLineOptions,
    modelYearOptions,
    offerTypeOptions,
    vehicleTypeOptions,
    offerRegionOptions,
  };
};

export default useFilterOptions;
